import { useState } from 'react';
import { useApi } from '../use_api';
import { useAuth } from '../auth/AuthProvider';
import {
  get_billing_config,
  get_office_app_data,
  create_checkout_session,
  ApiError,
  type BillingPlanOption,
} from '../api';

// F4 — billing. One page for everything billing:
//   1. "Your plan" — the tenant's current plan + status, relocated here from the Profile form (the
//      same office app-data `billing` group; Profile no longer renders it — no duplication).
//   2. The plan picker — offered plans from the BFF (GET /api/billing/config `mode:checkout` →
//      `plans[]`, a card per tier × a Monthly/Yearly toggle), POSTing the chosen slug to
//      /billing/checkout (docs/billing.md §6.8). `payment_link`/`none` are the legacy fallbacks.
//
// Pricing is visible to everyone; SETTING UP billing is owner/admin (POST is write-role-gated), so
// members see prices + a hint, not a button that would 403. The picker is hidden when a live
// subscription already exists (ACTIVE/LAPSED) — checkout would 409; changes go through "contact us"
// until a self-serve upgrade/Customer-Portal path exists.

const TIER_RANK: Record<string, number> = { starter: 0, plus: 1 };
const INTERVAL_LABEL: Record<string, string> = { month: 'Monthly', year: 'Yearly' };
const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  TRIAL: 'Free trial',
  LAPSED: 'Payment past due',
  CANCELLED: 'Cancelled',
  NOT_STARTED: 'No active plan',
};
const TIER_LABEL: Record<string, string> = { starter: 'Starter', plus: 'Plus' };

// Tier = the slug segment after the product prefix, before the interval. Only used for the
// deploy-skew fallback below (when an older BFF omits plans[] but sends the single plan slug).
function tier_of(slug: string): string {
  const after = slug.includes('.') ? slug.slice(slug.indexOf('.') + 1) : slug;
  return after.split('-')[0] ?? after;
}

function fmt_date(iso: unknown): string | null {
  if (typeof iso !== 'string') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

interface OfficeBilling {
  status?: string;
  plan_tier?: string;
  trial_ends_at?: string;
  cycle_ends_at?: string;
  payment_method_on_file?: boolean;
}

export function BillingView() {
  const { data } = useApi(() => get_billing_config(), []);
  const office = useApi(() => get_office_app_data(), []);
  const { state } = useAuth();
  const [busy, setBusy] = useState<string | null>(null); // slug whose checkout is in flight
  const [error, setError] = useState<string | null>(null);
  const [intervalChoice, setIntervalChoice] = useState<string | null>(null);

  const role = state.status === 'signed_in' ? state.session.role : 'member';
  const can_manage = role === 'owner' || role === 'admin';
  const mode = data?.mode ?? (data?.payment_link_url ? 'payment_link' : 'none');
  const success = new URLSearchParams(window.location.search).get('status') === 'success';

  // Current plan (relocated from Profile) — office's own billing mirror.
  const billing = (office.data?.data?.billing ?? null) as OfficeBilling | null;
  const has_plan = !!billing?.status && billing.status !== 'NOT_STARTED';
  // A live subscription already exists → backend checkout would 409 (no self-serve change yet), so
  // hide the picker for these and route changes through "contact us".
  const blocks_checkout = billing?.status === 'ACTIVE' || billing?.status === 'LAPSED';
  // Don't reveal a checkout CTA until the office mirror has loaded, so an ACTIVE/LAPSED tenant never
  // sees the picker flash before `blocks_checkout` is known. An office fetch error ends loading too,
  // so this is true with no billing data → no summary, picker shown; a stray click then hits the
  // handled 409 path.
  const billing_known = !office.loading;

  // Deploy-skew safety: if a checkout-mode BFF predates the plan-picker PR (back-compat single-plan
  // fields, no plans[]), synthesize one option so the CTA never vanishes (mirrors #108).
  const plans: BillingPlanOption[] =
    data?.plans && data.plans.length > 0
      ? data.plans
      : data?.mode === 'checkout' && data.plan
        ? [
            {
              plan: data.plan,
              tier: tier_of(data.plan),
              interval: 'month',
              name: data.plan_name,
              price_label: data.price_label,
            },
          ]
        : [];
  const intervals = [...new Set(plans.map((p) => p.interval))].sort((a, b) =>
    a === 'month' ? -1 : b === 'month' ? 1 : a.localeCompare(b),
  );
  const interval =
    intervalChoice ??
    plans.find((p) => p.plan === data?.default_plan)?.interval ??
    intervals[0] ??
    'month';
  const tiers = [...new Set(plans.map((p) => p.tier))].sort(
    (a, b) => (TIER_RANK[a] ?? 99) - (TIER_RANK[b] ?? 99) || a.localeCompare(b),
  );

  const show_picker = mode === 'checkout' && plans.length > 0 && !blocks_checkout && billing_known;

  async function start_checkout(slug: string) {
    setBusy(slug);
    setError(null);
    try {
      const { checkout_url } = await create_checkout_session(slug);
      window.location.href = checkout_url; // hand off to Stripe-hosted Checkout
    } catch (e) {
      const reason = e instanceof ApiError ? e.reason : '';
      setError(
        reason === 'already_subscribed'
          ? 'You already have an active subscription.'
          : reason === 'read_only'
            ? 'Only an owner or admin can set up billing.'
            : 'Could not start checkout — please try again, or contact us below.',
      );
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <h2 style={{ marginTop: 0 }}>Billing</h2>
      <p className="muted">Your Captomi plan, invoices, and payment method.</p>

      {success && (
        <p role="status" style={{ color: 'var(--ok, #137333)' }}>
          Thanks — we&apos;re setting up your subscription. It may take a moment to show as active.
        </p>
      )}

      {has_plan && billing && (
        <div className="card" style={{ margin: '0 0 18px', background: 'var(--surface-2, #f6f7f9)' }}>
          <h3 style={{ marginTop: 0 }}>Your plan</h3>
          <p style={{ marginBottom: 4 }}>
            <strong>{TIER_LABEL[billing.plan_tier ?? ''] ?? 'Captomi Office'}</strong>
            {' · '}
            {STATUS_LABEL[billing.status ?? ''] ?? billing.status}
          </p>
          {billing.status === 'TRIAL' && fmt_date(billing.trial_ends_at) && (
            <p className="muted" style={{ margin: 0 }}>Trial ends {fmt_date(billing.trial_ends_at)}</p>
          )}
          {(billing.status === 'ACTIVE' || billing.status === 'LAPSED') &&
            fmt_date(billing.cycle_ends_at) && (
              <p className="muted" style={{ margin: 0 }}>
                Current period ends {fmt_date(billing.cycle_ends_at)}
              </p>
            )}
          <p className="muted" style={{ margin: 0 }}>
            {billing.payment_method_on_file ? 'Payment method on file' : 'No payment method on file'}
          </p>
        </div>
      )}

      {show_picker && (
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ marginTop: 0 }}>{has_plan ? 'Choose your plan' : 'Choose a plan'}</h3>
          {intervals.length > 1 && (
            <div role="group" aria-label="Billing interval" style={{ marginBottom: 12 }}>
              {intervals.map((iv) => (
                <button
                  key={iv}
                  type="button"
                  className={`btn ${iv === interval ? '' : 'secondary'}`}
                  aria-pressed={iv === interval}
                  onClick={() => setIntervalChoice(iv)}
                  style={{ marginRight: 8 }}
                >
                  {INTERVAL_LABEL[iv] ?? iv}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {tiers.map((tier) => {
              const opt = plans.find((p) => p.tier === tier && p.interval === interval);
              if (!opt) return null; // no plan for this tier+interval (e.g. annual not set up yet)
              return (
                <div key={tier} className="card" style={{ flex: '1 1 240px', margin: 0 }}>
                  <h4 style={{ marginTop: 0 }}>{opt.name}</h4>
                  <p>
                    <strong>{opt.price_label}</strong>
                  </p>
                  {can_manage && (
                    <button
                      className="btn"
                      disabled={busy !== null}
                      onClick={() => start_checkout(opt.plan)}
                    >
                      {busy === opt.plan ? 'Starting…' : 'Set up payment'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {!can_manage && (
            <p className="muted">Ask an owner or admin on your team to set up billing.</p>
          )}
          {error && (
            <p role="alert" style={{ color: 'var(--err, #c5221f)' }}>
              {error}
            </p>
          )}
        </div>
      )}

      {mode === 'payment_link' && billing_known && !blocks_checkout && data?.payment_link_url && (
        <div style={{ marginBottom: 18 }}>
          <p>
            Start your <strong>{data?.plan_name}</strong> subscription
            {data?.price_label ? (
              <>
                {' '}— <strong>{data.price_label}</strong>
              </>
            ) : null}
            .
          </p>
          {can_manage ? (
            <a
              className="btn"
              style={{ display: 'inline-block', textDecoration: 'none' }}
              href={data.payment_link_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Set up payment
            </a>
          ) : (
            <p className="muted">Ask an owner or admin on your team to set up billing.</p>
          )}
        </div>
      )}

      <p>To change your plan or update your billing details, contact us and we will take care of it for you.</p>
      <a
        className="btn secondary"
        style={{ display: 'inline-block', textDecoration: 'none' }}
        href="mailto:support@captomi.com?subject=Billing%20request"
      >
        Contact Captomi about billing
      </a>
    </div>
  );
}
